import { useDismissEscape } from '@byecode/ui'
import type { ElementProps, FloatingContext, Middleware, MiddlewareReturn, Placement, ReferenceType } from '@floating-ui/react'
import { autoUpdate, flip, offset, shift, useFloating, useFloatingNodeId, useInteractions, useRole } from '@floating-ui/react'
import type { AnyRecord, FloatBlockAbstract } from '@lighthouse/core'
import { DevicePreviewEnum, POSITION } from '@lighthouse/core'
import { isInViewport, useDebounce } from '@lighthouse/tools'
import { useSetAtom } from 'jotai'
import { find, findIndex } from 'rambda'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useLatest, useUpdateEffect } from 'react-use'

import { PAGE_CONTAINER_HOST } from '../constants'
import { findPopoverTree, popoverListAtom, usePopoverTrigger } from '../hooks'

function centerMiddleware(): Middleware {
    return {
        name: 'center',
        fn(state): MiddlewareReturn {
            const { rects } = state

            // 获取参考元素和浮动元素的宽高
            const { reference, floating } = rects

            const nextX = reference.x + reference.width / 2 - floating.width / 2
            const nextY = reference.y + reference.height / 2 - floating.height / 2

            return {
                x: nextX,
                y: nextY
            }
        }
    }
}

const passthroughInteractionProps = <T extends AnyRecord | undefined>(v?: T) => v ?? ({} as T)

function createNoopInteractionProps(): ReturnType<typeof useInteractions> {
    // We only rely on getReferenceProps/getFloatingProps in our code paths.
    // Keep this as no-op passthrough to avoid registering any global listeners when no float exists.
    return {
        getReferenceProps: passthroughInteractionProps,
        getFloatingProps: passthroughInteractionProps,
        getItemProps: passthroughInteractionProps
    } as unknown as ReturnType<typeof useInteractions>
}

export interface MultipleFloatBoxContextProps {
    openedMap: Record<string, boolean>
    floatBlocks: FloatBlockAbstract[]
    disableDismiss?: boolean
    disabled?: boolean
    isOpenToggle?: boolean
    disableEscape?: boolean
    parentFloatId?: string
    rootFloatTargetId?: string
    previewType: DevicePreviewEnum

    updateDragNode?: () => void
    onOpenChange: (v: boolean, blockId: string) => void
    // checkRootChildren?: (id: string) => boolean
    children?: React.ReactNode
}

export interface MultipleFloatBoxValueContextProps extends MultipleFloatBoxContextProps {
    interactionProps: ReturnType<typeof useInteractions>
    reference: (node: HTMLDivElement) => void
    floating: (node: HTMLElement, blockId?: string) => void
    contextMap: Record<string, FloatingContext>
}
export interface FloatBoxProps extends Omit<MultipleFloatBoxContextProps, 'openedMap' | 'floatBlocks' | 'interactionProps'> {
    // openedMap: Record<string, boolean>
    opened: boolean
    floatBlock?: FloatBlockAbstract
}

const MultipleFloatBoxContext = createContext<null | MultipleFloatBoxValueContextProps>({
    reference: () => void 0,
    floating: () => void 0,
    contextMap: {},
    interactionProps: createNoopInteractionProps(),
    openedMap: {},
    floatBlocks: [],
    previewType: DevicePreviewEnum.DESKTOP,
    onOpenChange: () => void 0
}) as React.Context<MultipleFloatBoxValueContextProps>

function focusOnElement(totalTime: number, interval: number, selector: string): void {
    const startTime = Date.now()

    function tryFocus(): void {
        const element = document.querySelector<HTMLElement>(selector)

        if (element) {
            element.focus()
        } else if (Date.now() - startTime < totalTime) {
            setTimeout(tryFocus, interval)
        }
    }

    // 启动查找
    setTimeout(tryFocus, interval)
}

export const useFloatBox = (props: FloatBoxProps) => {
    const {
        floatBlock,
        opened,
        previewType,
        disableEscape,
        isOpenToggle,
        disabled,
        parentFloatId,
        rootFloatTargetId,
        onOpenChange,
        updateDragNode
    } = props
    const setPopoverList = useSetAtom(popoverListAtom)
    const { id: blockId, config } = floatBlock ?? {}
    const { breakPoint } = config ?? {}
    const { overlay } = breakPoint ?? {}
    const {
        showOn = 'click',
        dismiss: overlayDismiss = 'click',
        position = 'bottom',
        align = 'center',
        offset: overLayOffset,
        autoPosition,
        autoDistance = 0
    } = overlay ?? {}
    const limitRef = useLatest(autoDistance)
    const nodeId = useFloatingNodeId()
    const mainAxis = position === 'left' || position === 'right' ? overLayOffset?.[0] ?? 0 : overLayOffset?.[1] ?? 0
    const crossAxis = position === 'left' || position === 'right' ? overLayOffset?.[1] ?? 0 : overLayOffset?.[0] ?? 0

    const computedPlacement = useMemo<undefined | Placement>(() => {
        if (position === POSITION.center) {
            return
        }

        return align === 'center' ? position : `${position}-${align}`
    }, [align, position])

    const { context, refs, update, x, y, placement } = useFloating({
        nodeId,
        open: opened,
        onOpenChange: v => {
            onOpenChange(v, blockId ?? '')
        },
        placement: computedPlacement,
        strategy: 'fixed',
        middleware: [
            position === POSITION.center && centerMiddleware(),
            offset({ mainAxis, crossAxis }),
            autoPosition && flip({ boundary: document.querySelector<HTMLElement>(`${PAGE_CONTAINER_HOST}`) || document.body }),
            autoPosition &&
                shift({
                    limiter: {
                        fn: state => {
                            const { x, y, rects, elements } = state

                            // 获取自定义视窗 DOM 元素（默认使用 document.body）
                            const container = document.querySelector<HTMLElement>(`.applicationWrapper`) || document.body
                            const pageEle = document.querySelector<HTMLElement>(`${PAGE_CONTAINER_HOST}`)
                            if (!container || !pageEle) {
                                return state
                            }
                            const isRefInView = isInViewport(pageEle, elements.reference)
                            if (isRefInView) {
                                return state
                            }
                            const containerRect = container.getBoundingClientRect()
                            const pageRect = pageEle.getBoundingClientRect()
                            // 获取视口的宽度和高度
                            const viewWidth = pageRect.width
                            const viewHeight = pageRect.height
                            const viewY = previewType === DevicePreviewEnum.MOBILE ? containerRect.height - pageRect.height : 0
                            const viewX = 0

                            // 设置一个极限值
                            let limit = limitRef.current // 可根据需要调整该值

                            // 获取浮动元素的宽度和高度
                            const floatingWidth = rects.floating.width
                            const floatingHeight = rects.floating.height

                            // 获取容器的宽度和高度
                            const containerWidth = containerRect.width
                            const containerHeight = containerRect.height

                            const maxY = viewY + viewHeight - rects.floating.height - limit
                            const maxX = viewX + viewWidth - rects.floating.width - limit
                            const minY = viewY + limit
                            const minX = viewX + limit
                            // 动态计算 maxLimit：
                            // 基于浮动元素的宽度、高度和容器的可视区域宽度、高度来计算
                            const maxLimitX = (containerWidth - floatingWidth) / 2 // 容器宽度减去浮动元素宽度
                            const maxLimitY = (containerHeight - floatingHeight) / 2 // 容器高度减去浮动元素高度
                            const maxLimit = Math.min(maxLimitX, maxLimitY)

                            // 如果 limit 超过了最大 limit 值，则将其视为无效并重新设置为 0
                            if (limit > maxLimit) {
                                limit = 0 // 将 limit 设置为 0 或其他合适的默认值
                            }

                            // 判断 x 轴是否超出视口的左右边界
                            let correctedX = x

                            if (x < minX) {
                                correctedX = minX // 如果左边超出，设置为极限值
                            } else if (x > maxX) {
                                correctedX = maxX // 如果右边超出，设置为视口右侧极限值
                            }
                            // 判断 y 轴是否超出视口的上下边界
                            let correctedY = y
                            if (y < minY) {
                                correctedY = minY // 如果上边超出，设置为极限值
                            } else if (y > maxY) {
                                correctedY = maxY // 如果下边超出，设置为视口下侧极限值
                            }

                            // 防止 limit 过大导致位置异常
                            // correctedX = Math.max(limit, Math.min(correctedX, maxX))
                            // correctedY = Math.max(limit, Math.min(correctedY, maxY))

                            // 返回修正后的 x, y 坐标
                            return { x: correctedX, y: correctedY }
                        }
                    }
                })
        ],
        whileElementsMounted: autoUpdate
    })

    const role = useRole(context, { role: 'menu' })
    const trigger = usePopoverTrigger(context, {
        blockId,
        enabled: !disabled,
        enabledEscape: !disableEscape,
        openEvent: showOn,
        closeEvent: overlayDismiss,
        toggle: isOpenToggle,
        boundary: `${PAGE_CONTAINER_HOST}`
    })
    const escape = useDismissEscape(context, {
        enabled: !disableEscape
    })
    // const interactionProps = useInteractions([role, escape, trigger])

    const debounceY = useDebounce(y, 200)

    useUpdateEffect(() => {
        update()
    }, [config, previewType])

    useUpdateEffect(() => {
        updateDragNode?.()
    }, [debounceY])

    useEffect(() => {
        if (!blockId) {
            return
        }
        setPopoverList(draft => {
            if (parentFloatId) {
                const tree = findPopoverTree(draft, parentFloatId)
                const index = findIndex(v => v.id === blockId, tree?.children ?? [])
                if (opened && tree) {
                    const newPopover = { id: blockId, reference: refs.reference, floating: refs.floating, open: opened, children: [] }
                    tree.children.push(newPopover)
                    return draft
                }
                if (index !== -1) {
                    tree?.children.splice(index, 1)
                }
                return draft
            }

            if (opened) {
                const newPopover = { id: blockId, reference: refs.reference, floating: refs.floating, open: opened, children: [] }
                draft.push(newPopover)
                return draft
            }
            const index = findIndex(v => v.id === blockId, draft)
            if (index !== -1) {
                draft.splice(index, 1)
            }
            return draft
        })
    }, [blockId, opened, parentFloatId, refs.floating, refs.reference, rootFloatTargetId, setPopoverList])

    return {
        context,
        trigger,
        role,
        escape
    }
}

export const useFixedFloatBox = (props: FloatBoxProps) => {
    const { floatBlock, disabled, opened, parentFloatId, updateDragNode, onOpenChange } = props
    const { id: blockId, config } = floatBlock ?? {}
    const { breakPoint } = config ?? {}
    const { overlay } = breakPoint ?? {}
    const { showOn = 'click', closeClickOutside } = overlay ?? {}

    const reference = useRef<ReferenceType | null>(null)
    const floating = useRef<HTMLElement | null>(null)

    const setPopoverList = useSetAtom(popoverListAtom)

    const setReference = useCallback((node: ReferenceType) => {
        reference.current = node
    }, [])

    const setFloating = useCallback((node: HTMLElement) => {
        floating.current = node
    }, [])

    const debounceOpened = useDebounce(opened, 200)

    useUpdateEffect(() => {
        updateDragNode?.()
    }, [debounceOpened])

    const trigger: { reference: ElementProps['reference']; floating: ElementProps['floating'] } = useMemo(() => {
        return {
            reference: disabled
                ? {}
                : {
                      onClick(event) {
                          const isInTargetClick = (reference.current as HTMLElement)?.contains(event.target as HTMLElement)

                          if (!isInTargetClick) {
                              event.stopPropagation()
                              return
                          }
                          if (showOn === 'click') {
                              onOpenChange(true, blockId ?? '')
                              //   focusOnElement(2000, 300, `[data-node-id="${blockId}"]`)
                          }
                      },
                      onMouseEnter(event) {
                          if (showOn === 'hover') {
                              onOpenChange(true, blockId ?? '')
                              //   focusOnElement(2000, 300, `[data-node-id="${blockId}"]`)
                          }
                      }
                  },
            floating: disabled
                ? {}
                : {
                      onClick(event) {
                          if (closeClickOutside) {
                              const isFloatContainer =
                                  event.target instanceof HTMLDivElement && event.target.getAttribute('data-block-id') === blockId
                              isFloatContainer && opened && onOpenChange(false, blockId ?? '')
                          }
                      },
                      onKeyDown(event) {
                          if (event.key === 'Escape') {
                              event.stopPropagation()
                              opened && onOpenChange?.(false, blockId ?? '')
                          }
                      }
                  }
        }
    }, [disabled, showOn, onOpenChange, blockId, closeClickOutside, opened])

    const context = useMemo(
        () =>
            ({
                open: opened,
                refs: {
                    floating,
                    reference,
                    setFloating,
                    setReference
                },
                elements: {
                    domReference: reference.current
                }
            } as FloatingContext),
        [setFloating, setReference, opened]
    )

    useEffect(() => {
        if (!blockId) {
            return
        }
        setPopoverList(draft => {
            if (parentFloatId) {
                const tree = findPopoverTree(draft, parentFloatId)
                const index = findIndex(v => v.id === blockId, tree?.children ?? [])
                if (opened && tree) {
                    const newPopover = { id: blockId, reference, floating, open: opened, children: [] }
                    tree.children.push(newPopover)
                    return draft
                }
                if (index !== -1) {
                    tree?.children.splice(index, 1)
                }
                return draft
            }

            if (opened) {
                const newPopover = { id: blockId, reference, floating, open: opened, children: [] }
                draft.push(newPopover)
                return draft
            }
            const index = findIndex(v => v.id === blockId, draft)
            if (index !== -1) {
                draft.splice(index, 1)
            }
            return draft
        })
    }, [blockId, opened, parentFloatId, setPopoverList])

    return { setReference, setFloating, trigger, context }
}

const MultipleFloatBoxProviderNone: React.FC<MultipleFloatBoxContextProps> = props => {
    const {
        children,
        floatBlocks,
        openedMap,
        previewType,
        disableEscape,
        isOpenToggle,
        disabled,
        parentFloatId,
        rootFloatTargetId,
        updateDragNode,
        onOpenChange
    } = props

    const multipleFloatBoxValue = useMemo<MultipleFloatBoxValueContextProps>(
        () => ({
            openedMap: openedMap ?? {},
            floatBlocks: floatBlocks ?? [],
            previewType,
            disableEscape,
            disabled,
            isOpenToggle,
            parentFloatId,
            rootFloatTargetId,
            updateDragNode,
            onOpenChange,
            interactionProps: createNoopInteractionProps(),
            reference: (() => {}),
            floating: (() => {}),
            contextMap: {}
        }),
        [
            openedMap,
            floatBlocks,
            previewType,
            disableEscape,
            disabled,
            isOpenToggle,
            parentFloatId,
            rootFloatTargetId,
            updateDragNode,
            onOpenChange
        ]
    )

    return <MultipleFloatBoxContext.Provider value={multipleFloatBoxValue}>{children}</MultipleFloatBoxContext.Provider>
}

type RelativeFloatProviderProps = MultipleFloatBoxContextProps & {
    clickRelativeFloatBlock?: FloatBlockAbstract
    hoverRelativeFloatBlock?: FloatBlockAbstract
    fixedFloatBlock?: FloatBlockAbstract
}

const MultipleFloatBoxProviderClickNoFixedImpl: React.FC<RelativeFloatProviderProps> = props => {
    const {
        children,
        openedMap,
        floatBlocks,
        previewType,
        disableEscape,
        isOpenToggle,
        disabled,
        parentFloatId,
        rootFloatTargetId,
        onOpenChange,
        updateDragNode
    } = props
    const { clickRelativeFloatBlock } = props

    const commonProps = useMemo(
        () => ({ previewType, disableEscape, isOpenToggle, disabled, parentFloatId, rootFloatTargetId, onOpenChange, updateDragNode }),
        [previewType, disableEscape, isOpenToggle, disabled, parentFloatId, rootFloatTargetId, onOpenChange, updateDragNode]
    )

    const {
        role: clickRole,
        trigger: clickTrigger,
        escape: clickEscape,
        context: clickContext
    } = useFloatBox({
        ...commonProps,
        disabled: !clickRelativeFloatBlock || commonProps.disabled,
        opened: openedMap[clickRelativeFloatBlock?.id ?? ''],
        floatBlock: clickRelativeFloatBlock
    })

    const interactionProps = useInteractions([clickRole, clickTrigger, clickEscape])

    const reference = useCallback(
        (node: ReferenceType) => {
            clickContext.refs.setReference(node)
        },
        [clickContext.refs]
    )

    const floating = useCallback(
        (node: HTMLElement, blockId?: string) => {
            blockId === clickRelativeFloatBlock?.id && clickContext.refs.setFloating(node)
        },
        [clickContext.refs, clickRelativeFloatBlock?.id]
    )

    const multipleFloatBoxValue = useMemo(
        () => ({
            ...commonProps,
            reference,
            floating,
            contextMap: {
                [`${clickRelativeFloatBlock?.id ?? ''}`]: clickContext
            },
            interactionProps,
            openedMap,
            floatBlocks
        }),
        [commonProps, reference, floating, clickRelativeFloatBlock?.id, clickContext, interactionProps, openedMap, floatBlocks]
    )

    return <MultipleFloatBoxContext.Provider value={multipleFloatBoxValue}>{children}</MultipleFloatBoxContext.Provider>
}

const MultipleFloatBoxProviderClickWithFixedImpl: React.FC<RelativeFloatProviderProps> = props => {
    const {
        children,
        openedMap,
        floatBlocks,
        previewType,
        disableEscape,
        isOpenToggle,
        disabled,
        parentFloatId,
        rootFloatTargetId,
        onOpenChange,
        updateDragNode
    } = props
    const { clickRelativeFloatBlock, fixedFloatBlock } = props

    const commonProps = useMemo(
        () => ({ previewType, disableEscape, isOpenToggle, disabled, parentFloatId, rootFloatTargetId, onOpenChange, updateDragNode }),
        [previewType, disableEscape, isOpenToggle, disabled, parentFloatId, rootFloatTargetId, onOpenChange, updateDragNode]
    )

    const {
        role: clickRole,
        trigger: clickTrigger,
        escape: clickEscape,
        context: clickContext
    } = useFloatBox({
        ...commonProps,
        disabled: !clickRelativeFloatBlock || commonProps.disabled,
        opened: openedMap[clickRelativeFloatBlock?.id ?? ''],
        floatBlock: clickRelativeFloatBlock
    })

    const {
        trigger: fixedTrigger,
        context: fixedContext,
        setReference: fixedSetReference,
        setFloating: fixedSetFloating
    } = useFixedFloatBox({
        ...commonProps,
        disabled: !fixedFloatBlock || commonProps.disabled,
        opened: openedMap[fixedFloatBlock?.id ?? ''],
        floatBlock: fixedFloatBlock
    })

    const interactionProps = useInteractions([clickRole, clickTrigger, clickEscape, fixedTrigger])

    const reference = useCallback(
        (node: ReferenceType) => {
            clickContext.refs.setReference(node)
            fixedSetReference(node)
        },
        [clickContext.refs, fixedSetReference]
    )

    const floating = useCallback(
        (node: HTMLElement, blockId?: string) => {
            blockId === clickRelativeFloatBlock?.id && clickContext.refs.setFloating(node)
            blockId === fixedFloatBlock?.id && fixedSetFloating(node)
        },
        [clickContext.refs, clickRelativeFloatBlock?.id, fixedFloatBlock?.id, fixedSetFloating]
    )

    const multipleFloatBoxValue = useMemo(
        () => ({
            ...commonProps,
            reference,
            floating,
            contextMap: {
                [`${clickRelativeFloatBlock?.id ?? ''}`]: clickContext,
                [`${fixedFloatBlock?.id ?? ''}`]: fixedContext
            },
            interactionProps,
            openedMap,
            floatBlocks
        }),
        [
            commonProps,
            reference,
            floating,
            clickRelativeFloatBlock?.id,
            clickContext,
            fixedFloatBlock?.id,
            fixedContext,
            interactionProps,
            openedMap,
            floatBlocks
        ]
    )

    return <MultipleFloatBoxContext.Provider value={multipleFloatBoxValue}>{children}</MultipleFloatBoxContext.Provider>
}

const MultipleFloatBoxProviderHoverNoFixedImpl: React.FC<RelativeFloatProviderProps> = props => {
    const {
        children,
        openedMap,
        floatBlocks,
        previewType,
        disableEscape,
        isOpenToggle,
        disabled,
        parentFloatId,
        rootFloatTargetId,
        onOpenChange,
        updateDragNode
    } = props
    const { hoverRelativeFloatBlock } = props

    const commonProps = useMemo(
        () => ({ previewType, disableEscape, isOpenToggle, disabled, parentFloatId, rootFloatTargetId, onOpenChange, updateDragNode }),
        [previewType, disableEscape, isOpenToggle, disabled, parentFloatId, rootFloatTargetId, onOpenChange, updateDragNode]
    )

    const {
        role: hoverRole,
        trigger: hoverTrigger,
        escape: hoverEscape,
        context: hoverContext
    } = useFloatBox({
        ...commonProps,
        disabled: !hoverRelativeFloatBlock || commonProps.disabled,
        opened: openedMap[hoverRelativeFloatBlock?.id ?? ''],
        floatBlock: hoverRelativeFloatBlock
    })

    const interactionProps = useInteractions([hoverRole, hoverTrigger, hoverEscape])

    const reference = useCallback(
        (node: ReferenceType) => {
            hoverContext.refs.setReference(node)
        },
        [hoverContext.refs]
    )

    const floating = useCallback(
        (node: HTMLElement, blockId?: string) => {
            blockId === hoverRelativeFloatBlock?.id && hoverContext.refs.setFloating(node)
        },
        [hoverContext.refs, hoverRelativeFloatBlock?.id]
    )

    const multipleFloatBoxValue = useMemo(
        () => ({
            ...commonProps,
            reference,
            floating,
            contextMap: {
                [`${hoverRelativeFloatBlock?.id ?? ''}`]: hoverContext
            },
            interactionProps,
            openedMap,
            floatBlocks
        }),
        [commonProps, reference, floating, hoverRelativeFloatBlock?.id, hoverContext, interactionProps, openedMap, floatBlocks]
    )

    return <MultipleFloatBoxContext.Provider value={multipleFloatBoxValue}>{children}</MultipleFloatBoxContext.Provider>
}

const MultipleFloatBoxProviderHoverWithFixedImpl: React.FC<RelativeFloatProviderProps> = props => {
    const {
        children,
        openedMap,
        floatBlocks,
        previewType,
        disableEscape,
        isOpenToggle,
        disabled,
        parentFloatId,
        rootFloatTargetId,
        onOpenChange,
        updateDragNode
    } = props
    const { hoverRelativeFloatBlock, fixedFloatBlock } = props

    const commonProps = useMemo(
        () => ({ previewType, disableEscape, isOpenToggle, disabled, parentFloatId, rootFloatTargetId, onOpenChange, updateDragNode }),
        [previewType, disableEscape, isOpenToggle, disabled, parentFloatId, rootFloatTargetId, onOpenChange, updateDragNode]
    )

    const {
        role: hoverRole,
        trigger: hoverTrigger,
        escape: hoverEscape,
        context: hoverContext
    } = useFloatBox({
        ...commonProps,
        disabled: !hoverRelativeFloatBlock || commonProps.disabled,
        opened: openedMap[hoverRelativeFloatBlock?.id ?? ''],
        floatBlock: hoverRelativeFloatBlock
    })

    const {
        trigger: fixedTrigger,
        context: fixedContext,
        setReference: fixedSetReference,
        setFloating: fixedSetFloating
    } = useFixedFloatBox({
        ...commonProps,
        disabled: !fixedFloatBlock || commonProps.disabled,
        opened: openedMap[fixedFloatBlock?.id ?? ''],
        floatBlock: fixedFloatBlock
    })

    const interactionProps = useInteractions([hoverRole, hoverTrigger, hoverEscape, fixedTrigger])

    const reference = useCallback(
        (node: ReferenceType) => {
            hoverContext.refs.setReference(node)
            fixedSetReference(node)
        },
        [hoverContext.refs, fixedSetReference]
    )

    const floating = useCallback(
        (node: HTMLElement, blockId?: string) => {
            blockId === hoverRelativeFloatBlock?.id && hoverContext.refs.setFloating(node)
            blockId === fixedFloatBlock?.id && fixedSetFloating(node)
        },
        [hoverContext.refs, hoverRelativeFloatBlock?.id, fixedFloatBlock?.id, fixedSetFloating]
    )

    const multipleFloatBoxValue = useMemo(
        () => ({
            ...commonProps,
            reference,
            floating,
            contextMap: {
                [`${hoverRelativeFloatBlock?.id ?? ''}`]: hoverContext,
                [`${fixedFloatBlock?.id ?? ''}`]: fixedContext
            },
            interactionProps,
            openedMap,
            floatBlocks
        }),
        [
            commonProps,
            reference,
            floating,
            hoverRelativeFloatBlock?.id,
            hoverContext,
            fixedFloatBlock?.id,
            fixedContext,
            interactionProps,
            openedMap,
            floatBlocks
        ]
    )

    return <MultipleFloatBoxContext.Provider value={multipleFloatBoxValue}>{children}</MultipleFloatBoxContext.Provider>
}

const MultipleFloatBoxProviderClickHoverNoFixedImpl: React.FC<RelativeFloatProviderProps> = props => {
    const {
        children,
        openedMap,
        floatBlocks,
        previewType,
        disableEscape,
        isOpenToggle,
        disabled,
        parentFloatId,
        rootFloatTargetId,
        onOpenChange,
        updateDragNode
    } = props
    const { clickRelativeFloatBlock, hoverRelativeFloatBlock } = props

    const commonProps = useMemo(
        () => ({ previewType, disableEscape, isOpenToggle, disabled, parentFloatId, rootFloatTargetId, onOpenChange, updateDragNode }),
        [previewType, disableEscape, isOpenToggle, disabled, parentFloatId, rootFloatTargetId, onOpenChange, updateDragNode]
    )

    const {
        role: clickRole,
        trigger: clickTrigger,
        escape: clickEscape,
        context: clickContext
    } = useFloatBox({
        ...commonProps,
        disabled: !clickRelativeFloatBlock || commonProps.disabled,
        opened: openedMap[clickRelativeFloatBlock?.id ?? ''],
        floatBlock: clickRelativeFloatBlock
    })

    const {
        role: hoverRole,
        trigger: hoverTrigger,
        escape: hoverEscape,
        context: hoverContext
    } = useFloatBox({
        ...commonProps,
        disabled: !hoverRelativeFloatBlock || commonProps.disabled,
        opened: openedMap[hoverRelativeFloatBlock?.id ?? ''],
        floatBlock: hoverRelativeFloatBlock
    })

    const interactionProps = useInteractions([hoverRole, clickRole, hoverTrigger, clickTrigger, clickEscape, hoverEscape])

    const reference = useCallback(
        (node: ReferenceType) => {
            hoverContext.refs.setReference(node)
            clickContext.refs.setReference(node)
        },
        [clickContext.refs, hoverContext.refs]
    )

    const floating = useCallback(
        (node: HTMLElement, blockId?: string) => {
            blockId === hoverRelativeFloatBlock?.id && hoverContext.refs.setFloating(node)
            blockId === clickRelativeFloatBlock?.id && clickContext.refs.setFloating(node)
        },
        [clickContext.refs, clickRelativeFloatBlock?.id, hoverContext.refs, hoverRelativeFloatBlock?.id]
    )

    const multipleFloatBoxValue = useMemo(
        () => ({
            ...commonProps,
            reference,
            floating,
            contextMap: {
                [`${clickRelativeFloatBlock?.id ?? ''}`]: clickContext,
                [`${hoverRelativeFloatBlock?.id ?? ''}`]: hoverContext
            },
            interactionProps,
            openedMap,
            floatBlocks
        }),
        [
            commonProps,
            reference,
            floating,
            clickRelativeFloatBlock?.id,
            clickContext,
            hoverRelativeFloatBlock?.id,
            hoverContext,
            interactionProps,
            openedMap,
            floatBlocks
        ]
    )

    return <MultipleFloatBoxContext.Provider value={multipleFloatBoxValue}>{children}</MultipleFloatBoxContext.Provider>
}

const MultipleFloatBoxProviderClickHoverWithFixedImpl: React.FC<RelativeFloatProviderProps> = props => {
    const {
        children,
        openedMap,
        floatBlocks,
        previewType,
        disableEscape,
        isOpenToggle,
        disabled,
        parentFloatId,
        rootFloatTargetId,
        onOpenChange,
        updateDragNode
    } = props
    const { clickRelativeFloatBlock, hoverRelativeFloatBlock, fixedFloatBlock } = props

    const commonProps = useMemo(
        () => ({ previewType, disableEscape, isOpenToggle, disabled, parentFloatId, rootFloatTargetId, onOpenChange, updateDragNode }),
        [previewType, disableEscape, isOpenToggle, disabled, parentFloatId, rootFloatTargetId, onOpenChange, updateDragNode]
    )

    const {
        role: clickRole,
        trigger: clickTrigger,
        escape: clickEscape,
        context: clickContext
    } = useFloatBox({
        ...commonProps,
        disabled: !clickRelativeFloatBlock || commonProps.disabled,
        opened: openedMap[clickRelativeFloatBlock?.id ?? ''],
        floatBlock: clickRelativeFloatBlock
    })

    const {
        role: hoverRole,
        trigger: hoverTrigger,
        escape: hoverEscape,
        context: hoverContext
    } = useFloatBox({
        ...commonProps,
        disabled: !hoverRelativeFloatBlock || commonProps.disabled,
        opened: openedMap[hoverRelativeFloatBlock?.id ?? ''],
        floatBlock: hoverRelativeFloatBlock
    })

    const {
        trigger: fixedTrigger,
        context: fixedContext,
        setReference: fixedSetReference,
        setFloating: fixedSetFloating
    } = useFixedFloatBox({
        ...commonProps,
        disabled: !fixedFloatBlock || commonProps.disabled,
        opened: openedMap[fixedFloatBlock?.id ?? ''],
        floatBlock: fixedFloatBlock
    })

    const interactionProps = useInteractions([hoverRole, clickRole, hoverTrigger, clickTrigger, clickEscape, hoverEscape, fixedTrigger])

    const reference = useCallback(
        (node: ReferenceType) => {
            hoverContext.refs.setReference(node)
            clickContext.refs.setReference(node)
            fixedSetReference(node)
        },
        [clickContext.refs, hoverContext.refs, fixedSetReference]
    )

    const floating = useCallback(
        (node: HTMLElement, blockId?: string) => {
            blockId === hoverRelativeFloatBlock?.id && hoverContext.refs.setFloating(node)
            blockId === clickRelativeFloatBlock?.id && clickContext.refs.setFloating(node)
            blockId === fixedFloatBlock?.id && fixedSetFloating(node)
        },
        [
            clickContext.refs,
            clickRelativeFloatBlock?.id,
            fixedFloatBlock?.id,
            hoverContext.refs,
            hoverRelativeFloatBlock?.id,
            fixedSetFloating
        ]
    )

    const multipleFloatBoxValue = useMemo(
        () => ({
            ...commonProps,
            reference,
            floating,
            contextMap: {
                [`${clickRelativeFloatBlock?.id ?? ''}`]: clickContext,
                [`${hoverRelativeFloatBlock?.id ?? ''}`]: hoverContext,
                [`${fixedFloatBlock?.id ?? ''}`]: fixedContext
            },
            interactionProps,
            openedMap,
            floatBlocks
        }),
        [
            commonProps,
            reference,
            floating,
            clickRelativeFloatBlock?.id,
            clickContext,
            hoverRelativeFloatBlock?.id,
            hoverContext,
            fixedFloatBlock?.id,
            fixedContext,
            interactionProps,
            openedMap,
            floatBlocks
        ]
    )

    return <MultipleFloatBoxContext.Provider value={multipleFloatBoxValue}>{children}</MultipleFloatBoxContext.Provider>
}

const MultipleFloatBoxProviderClick: React.FC<RelativeFloatProviderProps> = props => {
    return props.fixedFloatBlock ? (
        <MultipleFloatBoxProviderClickWithFixedImpl {...props} />
    ) : (
        <MultipleFloatBoxProviderClickNoFixedImpl {...props} />
    )
}

const MultipleFloatBoxProviderHover: React.FC<RelativeFloatProviderProps> = props => {
    return props.fixedFloatBlock ? (
        <MultipleFloatBoxProviderHoverWithFixedImpl {...props} />
    ) : (
        <MultipleFloatBoxProviderHoverNoFixedImpl {...props} />
    )
}

const MultipleFloatBoxProviderClickHover: React.FC<RelativeFloatProviderProps> = props => {
    return props.fixedFloatBlock ? (
        <MultipleFloatBoxProviderClickHoverWithFixedImpl {...props} />
    ) : (
        <MultipleFloatBoxProviderClickHoverNoFixedImpl {...props} />
    )
}

const MultipleFloatBoxProviderFixedOnly: React.FC<RelativeFloatProviderProps> = props => {
    const {
        children,
        openedMap,
        floatBlocks,
        previewType,
        disableEscape,
        isOpenToggle,
        disabled,
        parentFloatId,
        rootFloatTargetId,
        onOpenChange,
        updateDragNode
    } = props
    const { fixedFloatBlock } = props

    const commonProps = useMemo(
        () => ({ previewType, disableEscape, isOpenToggle, disabled, parentFloatId, rootFloatTargetId, onOpenChange, updateDragNode }),
        [previewType, disableEscape, isOpenToggle, disabled, parentFloatId, rootFloatTargetId, onOpenChange, updateDragNode]
    )

    const { trigger, context, setReference, setFloating } = useFixedFloatBox({
        ...commonProps,
        disabled: !fixedFloatBlock || commonProps.disabled,
        opened: openedMap[fixedFloatBlock?.id ?? ''],
        floatBlock: fixedFloatBlock
    })

    const interactionProps = useInteractions([trigger])

    const reference = useCallback(
        (node: ReferenceType) => {
            setReference(node)
        },
        [setReference]
    )

    const floating = useCallback(
        (node: HTMLElement, blockId?: string) => {
            blockId === fixedFloatBlock?.id && setFloating(node)
        },
        [fixedFloatBlock?.id, setFloating]
    )

    const multipleFloatBoxValue = useMemo(
        () => ({
            ...commonProps,
            reference,
            floating,
            contextMap: {
                [`${fixedFloatBlock?.id ?? ''}`]: context
            },
            interactionProps,
            openedMap,
            floatBlocks
        }),
        [commonProps, reference, floating, fixedFloatBlock?.id, context, interactionProps, openedMap, floatBlocks]
    )

    return <MultipleFloatBoxContext.Provider value={multipleFloatBoxValue}>{children}</MultipleFloatBoxContext.Provider>
}

export const MultipleFloatBoxProvider: React.FC<MultipleFloatBoxContextProps> = props => {
    const { floatBlocks } = props
    const clickRelativeFloatBlock = useMemo(
        () =>
            find(
                d => d.config.breakPoint.overlay.showOn === 'click' && d.config.breakPoint.overlay.overlayType === 'relative',
                floatBlocks
            ),
        [floatBlocks]
    )
    const hoverRelativeFloatBlock = useMemo(
        () =>
            find(
                d => d.config.breakPoint.overlay.showOn === 'hover' && d.config.breakPoint.overlay.overlayType === 'relative',
                floatBlocks
            ),
        [floatBlocks]
    )
    const fixedFloatBlock = useMemo(() => find(d => d.config.breakPoint.overlay.overlayType === 'fixed', floatBlocks), [floatBlocks])

    const hasAnyFloat = (floatBlocks?.length ?? 0) > 0
    if (!hasAnyFloat) {
        return <MultipleFloatBoxProviderNone {...props} />
    }

    if (clickRelativeFloatBlock && hoverRelativeFloatBlock) {
        return (
            <MultipleFloatBoxProviderClickHover
                {...props}
                clickRelativeFloatBlock={clickRelativeFloatBlock}
                hoverRelativeFloatBlock={hoverRelativeFloatBlock}
                fixedFloatBlock={fixedFloatBlock}
            />
        )
    }
    if (clickRelativeFloatBlock) {
        return (
            <MultipleFloatBoxProviderClick {...props} clickRelativeFloatBlock={clickRelativeFloatBlock} fixedFloatBlock={fixedFloatBlock} />
        )
    }
    if (hoverRelativeFloatBlock) {
        return (
            <MultipleFloatBoxProviderHover {...props} hoverRelativeFloatBlock={hoverRelativeFloatBlock} fixedFloatBlock={fixedFloatBlock} />
        )
    }

    // Only fixed float blocks exist (no relative overlays) – avoid initializing relative floating logic.
    return <MultipleFloatBoxProviderFixedOnly {...props} fixedFloatBlock={fixedFloatBlock} />
}

export const useMultipleFloatBoxBlockContext = () => useContext(MultipleFloatBoxContext)

export const useFloatBoxContext = (id: string) => {
    const { openedMap, contextMap, onOpenChange, floating, reference, ...rest } = useContext(MultipleFloatBoxContext) ?? {}

    return useMemo(
        () => ({
            ...rest,
            reference,
            floating,
            opened: openedMap?.[id],
            context: contextMap?.[id],
            onOpenChange: (v: boolean) => onOpenChange(v, id)
        }),
        [contextMap, id, onOpenChange, openedMap, rest, reference, floating]
    )
}

const FloatBoxBlockContext = createContext<{
    id: string
    rootId: string
}>({
    id: '',
    rootId: ''
})

export const FloatBoxBlockProvider: React.FC<{ id: string; rootId: string; children: React.ReactNode }> = ({ children, rootId, id }) => {
    const value = useMemo(() => ({ rootId, id }), [id, rootId])
    return <FloatBoxBlockContext.Provider value={value}>{children}</FloatBoxBlockContext.Provider>
}

export const useFloatBoxBlockContext = () => useContext(FloatBoxBlockContext)
